import type {
  EventChargeMode,
  EventGuestBillingMode,
  EventPhotoPolicy,
  EventStatus,
  EventTemplateCategory,
  UserRole,
} from '@jq4race/shared';

// Keep in sync with the R1.4 status state machine (draft → scheduled → active → closed)
// and the shared EventChargeModeSchema.

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Active',
  closed: 'Closed',
};

export const EVENT_STATUS_TONE: Record<EventStatus, 'secondary' | 'info' | 'success' | 'outline'> =
  {
    draft: 'secondary',
    scheduled: 'info',
    active: 'success',
    closed: 'outline',
  };

export const CHARGE_MODE_LABEL: Record<EventChargeMode, string> = {
  per_attendee: 'Per attendee',
  forfait: 'Forfait (consolidated)',
  combination: 'Combination',
  free: 'Free',
};

export const CHARGE_MODE_OPTIONS: ReadonlyArray<{ value: EventChargeMode; label: string }> = [
  { value: 'per_attendee', label: CHARGE_MODE_LABEL.per_attendee },
  { value: 'forfait', label: CHARGE_MODE_LABEL.forfait },
  { value: 'combination', label: CHARGE_MODE_LABEL.combination },
  { value: 'free', label: CHARGE_MODE_LABEL.free },
];

/**
 * R1.4 field-editability matrix, restricted to the fields this form manages
 * (name/locationId/scheduledDate/scheduledTime/expectedCapacity/chargeMode).
 * `active` and `closed` freeze every core field: `active` only allows guest-list
 * and staffing changes (S2/S3, not part of this form); `closed` is fully immutable.
 */
export function isFieldEditable(
  status: EventStatus,
  field:
    | 'name'
    | 'locationId'
    | 'scheduledDate'
    | 'scheduledTime'
    | 'expectedCapacity'
    | 'chargeMode'
    | 'photoPolicy',
): boolean {
  if (status === 'draft') return true;
  if (status === 'scheduled') return field !== 'locationId';
  return false; // active | closed
}

export const EXPECTED_CAPACITY_HINT =
  'Informational only — check-in is never blocked once capacity is reached.';

// ── S2 — guest list (ADR-E4 billingMode, R2.1) ──────────────────────────────

export const GUEST_BILLING_MODE_LABEL: Record<EventGuestBillingMode, string> = {
  host: 'Host pays',
  self: 'Guest pays',
  free: 'Free',
};

export const GUEST_BILLING_MODE_OPTIONS: ReadonlyArray<{
  value: EventGuestBillingMode;
  label: string;
}> = [
  { value: 'host', label: GUEST_BILLING_MODE_LABEL.host },
  { value: 'self', label: GUEST_BILLING_MODE_LABEL.self },
  { value: 'free', label: GUEST_BILLING_MODE_LABEL.free },
];

/** Derived from `arrivedAt` — the API never returns a separate status field. */
export function guestStatusLabel(arrivedAt: string | null): 'Arrived' | 'Invited' {
  return arrivedAt ? 'Arrived' : 'Invited';
}

/** Guest-list mutations (add/bulk-import) are blocked once the event is closed (R1.4/R2.1). */
export function isGuestListEditable(status: EventStatus): boolean {
  return status !== 'closed';
}

// ── S3 — staffing (R3.1–R3.4) ────────────────────────────────────────────────

/** Only supervisor/operator are staffable as event valets, mirrors the backend
 * AssignStaffUseCase's ASSIGNABLE_ROLES exactly. */
export const STAFF_ASSIGNABLE_ROLES: UserRole[] = ['supervisor', 'operator'];

export const STAFF_ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  supervisor: 'Supervisor',
  operator: 'Operator/Valet',
};

/** Staffing mutations (assign/unassign) are blocked once the event is closed,
 * mirroring AssignStaffUseCase/UnassignStaffUseCase's closed-event guard. */
export function isStaffingEditable(status: EventStatus): boolean {
  return status !== 'closed';
}

// ── S1 (Templates-UI) — reusable event templates (R1.7/R1.8) ───────────────

export const EVENT_TEMPLATE_CATEGORY_LABEL: Record<EventTemplateCategory, string> = {
  catering: 'Catering',
  corporate: 'Corporate',
  wedding: 'Wedding',
};

export const EVENT_TEMPLATE_CATEGORY_OPTIONS: ReadonlyArray<{
  value: EventTemplateCategory;
  label: string;
}> = [
  { value: 'catering', label: EVENT_TEMPLATE_CATEGORY_LABEL.catering },
  { value: 'corporate', label: EVENT_TEMPLATE_CATEGORY_LABEL.corporate },
  { value: 'wedding', label: EVENT_TEMPLATE_CATEGORY_LABEL.wedding },
];

/** Mirrors CHARGE_MODE_LABEL — a template's `defaultConfig.chargeMode` is an
 * optional hint applied when an Event is created `fromTemplateId` (R1.7). */
export const EVENT_PHOTO_POLICY_LABEL: Record<EventPhotoPolicy, string> = {
  required_4: 'Required (4 photos)',
  deferred: 'Deferred (post-arrival)',
  optional: 'Optional (0-4 photos)',
};

export const EVENT_PHOTO_POLICY_OPTIONS: ReadonlyArray<{
  value: EventPhotoPolicy;
  label: string;
}> = [
  { value: 'required_4', label: EVENT_PHOTO_POLICY_LABEL.required_4 },
  { value: 'deferred', label: EVENT_PHOTO_POLICY_LABEL.deferred },
  { value: 'optional', label: EVENT_PHOTO_POLICY_LABEL.optional },
];

/** Surfaces the required_4 throughput tradeoff (event-photo-capture spec). */
export const EVENT_PHOTO_POLICY_HINT =
  'Required (4 photos) captures one photo per angle before check-in completes — slows down high-volume events. Deferred and Optional keep check-in fast.';

/** R1.8 assumption (spec): template creation is org_admin-only — supervisors
 * may only apply a template via CreateEventUseCase's templateId, mirroring
 * the backend AdminEventTemplatesController.create()'s requireOrgAdminActor. */
export function isTemplateCreateAllowed(role: string | null | undefined): boolean {
  return role === 'org_admin';
}
